import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import cx from './cx';
import '../../styles/app-ui.scss';

/**
 * Coquille Bootstrap unique pour les modaux métier.
 */
const AppModal = ({
  show = false,
  title,
  onClose,
  footer,
  children,
  size,
  centered = false,
  scrollable = false,
  className = '',
  dialogClassName = '',
  contentClassName = '',
  bodyClassName = '',
  headerClassName = '',
  footerClassName = '',
  header,
  wrapBody = true,
  closeOnOverlay = true,
  closeDisabled = false,
  labelledBy,
  contentAs: ContentTag = 'div',
  contentProps = {},
}) => {
  const generatedTitleId = useId();
  const titleId = labelledBy || generatedTitleId;
  const overlayRef = useRef(null);
  const overlayPointerUpRef = useRef(null);

  const clearOverlayPointerUp = () => {
    overlayPointerUpRef.current?.();
    overlayPointerUpRef.current = null;
  };

  useEffect(() => {
    if (!show) return undefined;

    const onKeyDown = (event) => {
      if (event.key !== 'Escape' || closeDisabled) return;
      const overlays = document.querySelectorAll('.app-modal-overlay');
      if (overlays.length && overlays[overlays.length - 1] !== overlayRef.current) return;
      onClose?.();
    };

    window.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearOverlayPointerUp();
      document.body.style.overflow = previousOverflow;
    };
  }, [show, onClose, closeDisabled]);

  if (!show) return null;

  const handleOverlayPointerDown = (event) => {
    if (!closeOnOverlay || closeDisabled) return;
    if (event.button != null && event.button !== 0) return;
    if (event.target !== event.currentTarget) return;

    const overlay = event.currentTarget;
    clearOverlayPointerUp();

    const handlePointerUp = (upEvent) => {
      clearOverlayPointerUp();
      if (upEvent.target === overlay) {
        onClose?.();
      }
    };

    document.addEventListener('pointerup', handlePointerUp);
    overlayPointerUpRef.current = () => {
      document.removeEventListener('pointerup', handlePointerUp);
    };
  };

  const node = (
    <div
      ref={overlayRef}
      className={cx('modal', 'show', 'd-block', 'app-modal-overlay', className)}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title != null ? titleId : labelledBy}
      onPointerDown={handleOverlayPointerDown}
    >
      <div
        className={cx(
          'modal-dialog',
          size && `modal-${size}`,
          centered && 'modal-dialog-centered',
          scrollable && 'modal-dialog-scrollable',
          dialogClassName,
        )}
        onClick={(event) => event.stopPropagation()}
        role="document"
      >
        <ContentTag className={cx('modal-content', contentClassName)} {...contentProps}>
          {header !== undefined ? header : (title != null ? (
            <div className={cx('modal-header', headerClassName)}>
              <h5 className="modal-title" id={titleId}>{title}</h5>
              {onClose ? (
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Fermer"
                  onClick={onClose}
                  disabled={closeDisabled}
                />
              ) : null}
            </div>
          ) : null)}
          {wrapBody ? (
            <div className={cx('modal-body', bodyClassName)}>{children}</div>
          ) : children}
          {footer != null ? (
            <div className={cx('modal-footer', footerClassName)}>{footer}</div>
          ) : null}
        </ContentTag>
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default AppModal;
